import * as React from 'react'
import { createRef, useMemo } from 'react'

import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import {
  selectOpportunityDetail,
  useGetOpportunityDetailOnMount,
} from '@user/store/opportunity'
import { ENTITY_TYPES } from '@sales-center/common'
import { IAddOpportunityFormModel } from '../typings'
import { initialValues, useValidate } from '../formik'
import LinearProgress from '@mui/material/LinearProgress'
import { useData, useGetOpportunityTitle, useOnSubmit } from '../hooks'
import { customValuesToFormModel } from '@user/components/CustomFields'
import { AddOpportunityForm } from '../AddOpportunityForm/AddOpportunityForm'
import { SOLD_SERVICES_CONTENT_MODE } from '@user/components/Opportunities/SoldServicesDrawer/SoldServicesContent/typings'

import { useSelectCustomFieldData } from '@user/store/customFields'
import { useGetLeadOppProductsOnMount } from '@user/store/leadOppProducts'
import { pestNamesToIds, selectAllTargetPests } from '@user/store/targetPests'
import { useGetEntityLocations } from '@user/store/entityLocations/hooks/useGetEntityLocations'
import { useNoteCodes } from '@user/components/Opportunities/AddOpportunityPage/AddOpportunityForm/hooks/useNoteCodes'
import { useGetSalesFunnelData } from '@user/components/Settings/SalesFunnels/hooks'

type Props = {
  opportunityId: number
}

export function AddOpportunityFromExistingOpportunity(props: Props) {
  const { opportunityId } = props

  const dataLoading = useData()

  const onSubmit = useOnSubmit({
    entityType: ENTITY_TYPES.Opportunity,
    isOppFromOpp: true,
  })

  const oppLoadStatus = useGetOpportunityDetailOnMount(opportunityId)
  const oppDetail = useSelector(selectOpportunityDetail)

  const { loading, billTo, serviceLocations } = useGetEntityLocations(
    ENTITY_TYPES.Opportunity,
    opportunityId,
  )

  const productsLoadStatus = useGetLeadOppProductsOnMount(
    opportunityId,
    ENTITY_TYPES.Opportunity,
  )

  const allTargetPests = useSelector(selectAllTargetPests)

  const title = useGetOpportunityTitle(billTo)

  const { customFields } = useSelectCustomFieldData()

  const { noteCodes } = useNoteCodes()

  const { firstStage } = useGetSalesFunnelData(
    ENTITY_TYPES.Opportunity,
    oppDetail?.salesFunnelId || 0,
  )

  const hydratedInitialValues = useMemo<
    IAddOpportunityFormModel | undefined
  >(() => {
    if (oppDetail) {
      const targetPests = pestNamesToIds(allTargetPests, oppDetail.targetPests)

      return {
        ...initialValues,
        billTo: billTo,
        billToExternalLinkId: billTo?.externalLinkId ?? '',
        campaignId: oppDetail.campaignId,
        ownerId: oppDetail.assigneeId?.toString() ?? '',
        referral:
          oppDetail.referralSummary && oppDetail.referralSummary.referralType
            ? oppDetail.referralSummary
            : null,
        salesFunnelId: oppDetail.salesFunnelId.toString(),
        salesTeamId: oppDetail.salesTeamId.toString(),
        salesFunnelStageId: oppDetail.salesFunnelStageId,
        serviceLocations: serviceLocations,
        sourceEntityId: opportunityId,
        opportunityId: opportunityId,
        targetPests,
        customFields: customValuesToFormModel(customFields),
        externalLocationId: oppDetail.externalLocationId,
        opportunityStageId: firstStage?.id.toString() || '',
        addNoteToPestPac: Boolean(oppDetail.externalLocationId),
        serviceLocation:
          serviceLocations.length === 1
            ? serviceLocations[0].id.toString()
            : '',
        noteCode: Boolean(oppDetail.externalLocationId)
          ? noteCodes.find((code) => code.isDefault)?.name
          : '',
      }
    }
  }, [
    oppDetail,
    allTargetPests,
    billTo,
    serviceLocations,
    opportunityId,
    customFields,
    firstStage?.id,
    noteCodes,
  ])

  const externalSvcLocId = serviceLocations?.[0]?.externalLinkId ?? ''

  const showForm =
    !loading &&
    oppLoadStatus !== 'pending' &&
    billTo &&
    serviceLocations &&
    !dataLoading &&
    productsLoadStatus !== 'pending' &&
    !!hydratedInitialValues

  const ref = createRef<HTMLDivElement>()

  const { validate } = useValidate()

  return showForm ? (
    <Formik
      onSubmit={onSubmit}
      validate={validate}
      initialValues={hydratedInitialValues}
      validateOnChange={true}
    >
      <AddOpportunityForm
        billTo={billTo}
        externalSvcLocId={externalSvcLocId}
        serviceLocations={serviceLocations}
        serviceLocationsLoading={loading}
        innerRef={ref}
        title={title}
        entityId={opportunityId}
        entityBranchId={oppDetail?.branchId}
        entityType={ENTITY_TYPES.Opportunity}
        type={SOLD_SERVICES_CONTENT_MODE.ConvertLead}
        noteCodes={noteCodes}
      />
    </Formik>
  ) : (
    <LinearProgress />
  )
}
